import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import useGlobalStore from "../../../../store/global.store";
import { useRemote } from "../../../../hooks/useRemote";
import { useTV } from "../../../../hooks/useTV";
import { globalStyles } from "../../../../utils/global-styles";
import { useConfig } from "../../../../hooks/useConfig";
import moment from "moment-timezone";
import VipSubCheck from "../VipSubCheck";
import axios from "../../../../config/axios";

const VipPiano = ({ device }) => {
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const globalDevice = useGlobalStore((state) => state.device);
  const { updateView } = useRemote();
  const { isTv } = useTV();
  const { config } = useConfig();
  const pianoChoice = Number(config?.pianostreamURL);
  const pianoLiveSrc =
    pianoChoice === 1
      ? isTv
        ? "https://rtmp3.mywaitserver.com/live/player/1080p/"
        : "https://rtmp3.mywaitserver.com/live/player/720p/"
      : pianoChoice === 2
      ? isTv
        ? "https://rtmp2.mywaitserver.com/live/player/1080p/"
        : "https://rtmp2.mywaitserver.com/live/player/720p/"
      : pianoChoice === 3
      ? isTv
        ? "https://rtmp.mywaitserver.com/live/player/"
        : "https://rtmp.mywaitserver.com/live/player/"
      : isTv
      ? "https://rtmp2.mywaitserver.com/live/player/1080p/" // default fallback
      : "https://rtmp2.mywaitserver.com/live/player/720p/";

  // Use device prop if provided, otherwise fall back to global store
  const currentDevice = device || globalDevice;

  const getSavedVolume = () => {
    const raw = localStorage.getItem("volume");
    const v = parseFloat(raw ?? "1");
    return isNaN(v) ? 1 : Math.max(0, Math.min(1, v));
  };

  const pianoAudioRef = useRef(null);
  const liveExitRef = useRef(null);
  const splashExitRef = useRef(null);

  const [focusedButton, setFocusedButton] = useState("exit");
  const [livePianoRefreshValue, setLivePianoRefreshValue] = useState(null);

  const isShowLive = config?.Piano_Show_On === true;

  const handleExitClick = useCallback(() => {
    updateView("VIP");
  }, [updateView]);

  useEffect(() => {
    if (config?.Live_Piano_Refresh_Value) {
      setLivePianoRefreshValue(config?.Live_Piano_Refresh_Value);

      if (
        livePianoRefreshValue !== null &&
        livePianoRefreshValue !== config?.Live_Piano_Refresh_Value
      ) {
        console.log("livePianoRefreshValue", livePianoRefreshValue);
        console.log(
          "config?.Live_Piano_Refresh_Value",
          config?.Live_Piano_Refresh_Value
        );
        localStorage.setItem("memClear", "yes");
        window.location.reload();
      }
    }
  }, [config?.Live_Piano_Refresh_Value, livePianoRefreshValue]);

  // API call to increment viewer count when component mounts
  useEffect(() => {
    if (currentDevice?._id) {
      axios
        .post("/live-piano/increment-viewer-count", {
          device_id: currentDevice._id,
        })
        .catch((error) => {
          console.error("Failed to increment viewer count:", error);
          Sentry.captureException(error);
        });
    }

    // Cleanup function to decrement viewer count when component unmounts
    return () => {
      if (currentDevice?._id) {
        axios
          .post("/live-piano/decrement-viewer-count", {
            device_id: currentDevice._id,
          })
          .catch((error) => {
            console.error("Failed to decrement viewer count:", error);
            Sentry.captureException(error);
          });
      }
    };
  }, [currentDevice?._id]);

  useEffect(() => {
    setShowBottomBar(false);
    try {
      if (pianoAudioRef.current) {
        const vol = getSavedVolume();
        pianoAudioRef.current.volume = vol;
        if (!isShowLive && vol > 0) {
          pianoAudioRef.current.play().catch(() => {});
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [device, setShowBottomBar, isShowLive]);

  useEffect(() => {
    try {
      const el = pianoAudioRef.current;
      if (!el) return;
      if (isShowLive) {
        el.pause();
      } else {
        const vol = getSavedVolume();
        el.volume = vol;
        if (vol > 0) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [isShowLive]);

  useEffect(() => {
    if (isTv && isShowLive) {
      Promise.resolve().then(() => {
        liveExitRef.current?.focus();
        setFocusedButton("exit");
      });
    }
  }, [isTv, isShowLive]);

  useEffect(() => {
    if (!(isTv && isShowLive)) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isSelect =
        e.key === "Enter" ||
        e.key === "OK" ||
        e.key === "Select" ||
        kc === 13 ||
        kc === 23 ||
        kc === 66;
      if (isSelect) {
        e.preventDefault();
        e.stopPropagation();
        handleExitClick();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, isShowLive, handleExitClick]);

  useEffect(() => {
    const isShowingSplash = isTv && !isShowLive;
    if (isShowingSplash) {
      Promise.resolve().then(() => {
        splashExitRef.current?.focus();
        setFocusedButton("exit");
      });
    }
  }, [isTv, isShowLive]);

  useEffect(() => {
    const isShowingSplash = isTv && !isShowLive;
    if (!isShowingSplash) return;
    const handleKeyDown = (e) => {
      const kc = e.keyCode;
      const isSelect =
        e.key === "Enter" ||
        e.key === "OK" ||
        e.key === "Select" ||
        kc === 13 ||
        kc === 23 ||
        kc === 66;
      if (isSelect) {
        e.preventDefault();
        e.stopPropagation();
        handleExitClick();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isTv, isShowLive, handleExitClick]);

  const getButtonStyles = (buttonId, baseStyle) => {
    if (isTv && focusedButton === buttonId) {
      return {
        ...baseStyle,
        color: "white",
        borderColor: "#3ADF3C",
        outline: "none",
        border: "6px solid #3ADF3C",
      };
    }
    return baseStyle;
  };

  const nextShowText = useMemo(() => {
    const shows = config?.pianoShows;
    if (!Array.isArray(shows) || shows.length === 0) return null;
    const nowET = moment.tz("America/New_York");
    const parsed = shows
      .map((s) => {
        if (!s?.date || !s?.time) return null;
        return moment.tz(
          `${s.date} ${s.time}`,
          "YYYY-MM-DD HH:mm",
          "America/New_York"
        );
      })
      .filter((m) => m?.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());
    if (parsed.length === 0) return null;
    const next =
      parsed.find((m) => m.valueOf() >= nowET.valueOf()) ||
      parsed[parsed.length - 1];
    const dateStr = next.format("MMMM D, YYYY");
    const timeStr = next.format("h:mm A");
    return `${dateStr} @ ${timeStr} (EST)`;
  }, [config?.pianoShows]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#380a01",
      }}
    >
      {isShowLive ? (
        <>
          <iframe
            title="Piano Live"
            src={pianoLiveSrc}
            allow="autoplay; fullscreen"
            allowFullScreen
            style={{
              position: "absolute",
              inset: 0,
              width: "100vw",
              height: "100vh",
              border: "none",
              pointerEvents: "none",
            }}
          />
          <button
            ref={liveExitRef}
            onClick={handleExitClick}
            onContextMenu={(e) => e.preventDefault()}
            onFocus={() => setFocusedButton("exit")}
            onBlur={() => setFocusedButton(null)}
            tabIndex={0}
            style={getButtonStyles("exit", {
              ...globalStyles.button(isTv),
              position: "absolute",
              zIndex: 2000,
              right: isTv ? 20 : 16,
              bottom: isTv ? 20 : 16,
              backgroundColor: "rgba(0,0,0,0.35)",
              height: isTv ? "70px" : "50px",
              fontSize: "30px",
              borderColor: "white",
              ...(isTv ? { marginRight: "40px" } : {}),
            })}
          >
            <span style={{ color: "white" }}>EXIT</span>
          </button>
        </>
      ) : (
        <>
          <video
            src={
              isTv
                ? "https://cdn.mywaitserver.live/piano.mp4"
                : "https://cdn.mywaitserver.live/pianodisplay.mp4"
            }
            autoPlay
            loop
            muted
            style={{ width: "100vw", height: "100vh" }}
          />
          <audio
            src="https://cdn.mywaitserver.live/pianomusic1.mp3"
            ref={pianoAudioRef}
            loop
            onPause={() => {
              console.log("pianoAudioRef onPause");
            }}
            onEnded={() => {
              console.log("pianoAudioRef onEnded");
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "0px",
              left: "10px",
              zIndex: 1000,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              padding: isTv ? "10px 20px" : "0px 20px",
            }}
          >
            <span
              style={{
                fontSize: isTv ? "50px" : "33px",
                color: "white",
                fontWeight: "bold",
                fontFamily: "Lato",
              }}
            >
              As seen in Disney Springs, Universal City Walk, & Jelly Rolls!
            </span>
            <button
              ref={splashExitRef}
              onClick={handleExitClick}
              onContextMenu={(e) => e.preventDefault()}
              onFocus={() => setFocusedButton("exit")}
              onBlur={() => setFocusedButton(null)}
              tabIndex={0}
              style={getButtonStyles("exit", {
                ...globalStyles.button(isTv),
                borderColor: "white",
                backgroundColor: "transparent",
                height: isTv ? "70px" : "50px",
                fontSize: "30px",
                ...(isTv ? { marginRight: "40px" } : {}),
              })}
            >
              <span style={{ color: "white" }}>EXIT</span>
            </button>
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: "0px 20px",
              paddingTop: isTv ? "30px" : "20px",
            }}
          >
            <span
              style={{
                fontSize: isTv ? "55px" : "38px",
                fontWeight: "bold",
                fontFamily: "Lato",
                color: "white",
              }}
            >
              Next Show: {nextShowText ?? "TBA"}
            </span>
          </div>
        </>
      )}
      <VipSubCheck />
    </div>
  );
};

export default VipPiano;
